import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import posthog from "@/lib/posthog";
import { resolveDateFilter, DateFilterKey } from "@/utils/dateFilters";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Landmark, CreditCard } from "lucide-react";
import apiClient from "@/utils/httpClient";
import { formatAmount } from "@/utils/expenseFormatters";
import TransactionFilters from "./TransactionFilters";
import TransactionTable from "./TransactionTable";
import StatementView from "./StatementView";
import ReconciliationModal from "./ReconciliationModal";
import {
  BankAccountDto,
  BankTransactionDto,
  BankTransactionPagination,
  BankTransactionApiResponse,
  Category,
} from "@blinkbooks/types";

const DEFAULT_PAGINATION: BankTransactionPagination = {
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
};

// Soft-tinted icon palette matching the HTML design
const ACCT_PALETTES = [
  { bg: "#FAC775", text: "#633806" }, // amber
  { bg: "#B5D4F4", text: "#0C447C" }, // blue
  { bg: "#C0DD97", text: "#27500A" }, // green
  { bg: "#D3D1C7", text: "#444441" }, // gray
  { bg: "#C4B5F4", text: "#3730A3" }, // purple
  { bg: "#A7F3D0", text: "#065F46" }, // teal
];
const CREDIT_PALETTE = { bg: "#F5C4B3", text: "#712B13" }; // coral — always for credit

const getAccountIconStyle = (account: BankAccountDto) => {
  if (account.type === "CREDIT") return CREDIT_PALETTE;
  let hash = 0;
  for (let i = 0; i < account.name.length; i++) {
    hash = account.name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ACCT_PALETTES[Math.abs(hash) % ACCT_PALETTES.length];
};

interface BankingProps {
  dateFilterKey: DateFilterKey | '';
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
}

const Banking = ({ dateFilterKey, dateCustomFrom, dateCustomTo, onDateFilterChange }: BankingProps) => {
  const [banks, setBanks] = useState<BankAccountDto[]>([]);
  const [transactionsByBank, setTransactionsByBank] = useState<Record<string, BankTransactionDto[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [categoriesFetched, setCategoriesFetched] = useState(false);

  const [viewMode, setViewMode] = useState<'table' | 'statement'>('table');

  const [paginationByBank, setPaginationByBank] = useState<Record<string, BankTransactionPagination>>({});
  const [currentPageByBank, setCurrentPageByBank] = useState<Record<string, number>>({});
  const [openingBalanceByBank, setOpeningBalanceByBank] = useState<Record<string, number>>({});
  const [finalBalanceByBank, setFinalBalanceByBank] = useState<Record<string, number>>({});

  const [accountTypeFilter, setAccountTypeFilter] = useState<'all' | 'banks' | 'cards'>('all');

  const [merchantName, setMerchantName] = useState<string>("");
  const [directionFilter, setDirectionFilter] = useState<string>("all");
  const [intercompanyOnly, setIntercompanyOnly] = useState<boolean>(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [reconcileTransactionId, setReconcileTransactionId] = useState<string | null>(null);
  const [entityId, setEntityId] = useState<string>("");

  const fetchTransactions = useCallback(async (
    bankId: string,
    page: number = 1,
    filters?: {
      merchantName?: string;
      direction?: string;
      startDate?: string;
      endDate?: string;
      intercompanyOnly?: boolean;
    },
    sortOrder: 'ASC' | 'DESC' = 'DESC',
  ) => {
    try {
      setLoading(true);
      const params: Record<string, string | number | boolean> = {
        bankId,
        page,
        limit: 50,
        sortOrder,
      };

      if (filters?.merchantName) params.merchantName = filters.merchantName;
      if (filters?.direction && filters.direction !== "all") params.direction = filters.direction;
      if (filters?.startDate) params.startDate = filters.startDate;
      if (filters?.endDate) params.endDate = filters.endDate;
      if (filters?.intercompanyOnly) params.intercompanyOnly = true;

      const response = await apiClient.getRaw<BankTransactionApiResponse>('/banking/transactions', params);

      if (response && response.success) {
        const data = response.data || [];
        const paginationData = response.pagination || DEFAULT_PAGINATION;
        setTransactionsByBank(prev => ({ ...prev, [bankId]: data }));
        setPaginationByBank(prev => ({ ...prev, [bankId]: paginationData }));
        setCurrentPageByBank(prev => ({ ...prev, [bankId]: paginationData.page }));
        if (response.openingBalance !== undefined) {
          setOpeningBalanceByBank(prev => ({ ...prev, [bankId]: response.openingBalance! }));
          // Derive current balance from last (most recent) transaction's running balance
          if (data.length > 0) {
            let bal = response.openingBalance!;
            for (const t of [...data].reverse()) {
              bal += t.direction === "IN" ? Number(t.amount) : -Number(t.amount);
            }
            setFinalBalanceByBank(prev => ({ ...prev, [bankId]: bal }));
          }
        }
      } else {
        setTransactionsByBank(prev => ({ ...prev, [bankId]: [] }));
        setPaginationByBank(prev => ({ ...prev, [bankId]: DEFAULT_PAGINATION }));
        setCurrentPageByBank(prev => ({ ...prev, [bankId]: 1 }));
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setTransactionsByBank(prev => ({ ...prev, [bankId]: [] }));
    } finally {
      setLoading(false);
    }
  }, []);

  const getCurrentFilters = useCallback(() => {
    const filters: {
      merchantName?: string;
      direction?: string;
      startDate?: string;
      endDate?: string;
      intercompanyOnly?: boolean;
    } = {};
    if (merchantName) filters.merchantName = merchantName;
    if (directionFilter !== "all") filters.direction = directionFilter;
    if (intercompanyOnly) filters.intercompanyOnly = true;
    const resolved = resolveDateFilter(dateFilterKey, dateCustomFrom, dateCustomTo);
    if (resolved) { filters.startDate = resolved.startDate; filters.endDate = resolved.endDate; }
    return filters;
  }, [merchantName, directionFilter, intercompanyOnly, dateFilterKey, dateCustomFrom, dateCustomTo]);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoading(true);
        const bankList = await apiClient.get<BankAccountDto[]>('/banking/banks');
        setBanks(bankList || []);
      } catch (error) {
        console.error('Failed to fetch banks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Auto-select first bank once list loads
  useEffect(() => {
    if (banks.length > 0 && !activeTab) {
      setActiveTab(String(banks[0].id));
    }
  }, [banks, activeTab]);

  const currentSortOrder: 'ASC' | 'DESC' = 'DESC';

  useEffect(() => {
    if (activeTab && !transactionsByBank[activeTab]) {
      fetchTransactions(activeTab, 1, getCurrentFilters(), currentSortOrder);
    }
  }, [activeTab, transactionsByBank, fetchTransactions, getCurrentFilters, currentSortOrder]);

  const isFirstDateRender = useRef(true);
  useEffect(() => {
    if (isFirstDateRender.current) { isFirstDateRender.current = false; return; }
    setTransactionsByBank({});
    setPaginationByBank({});
  }, [dateFilterKey, dateCustomFrom, dateCustomTo]);

  useEffect(() => {
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, []);

  const transactions = useMemo(() => {
    return activeTab ? (transactionsByBank[activeTab] || []) : [];
  }, [activeTab, transactionsByBank]);

  const currentPagination = useMemo(() => {
    return activeTab ? (paginationByBank[activeTab] || DEFAULT_PAGINATION) : DEFAULT_PAGINATION;
  }, [activeTab, paginationByBank]);

  const currentPage = useMemo(() => {
    return activeTab ? (currentPageByBank[activeTab] || 1) : 1;
  }, [activeTab, currentPageByBank]);

  const visibleBanks = useMemo(() => {
    const filtered = accountTypeFilter === 'all'
      ? banks
      : banks.filter(b => accountTypeFilter === 'cards' ? b.type === 'CREDIT' : b.type !== 'CREDIT');
    return [...filtered].sort((a, b) =>
      Math.abs(Number(b.bookBalance) || 0) - Math.abs(Number(a.bookBalance) || 0)
    );
  }, [banks, accountTypeFilter]);

  // Keep activeTab pointing at a visible account when the filter changes
  useEffect(() => {
    if (visibleBanks.length === 0) { setActiveTab(null); return; }
    if (!activeTab || !visibleBanks.find(b => String(b.id) === activeTab)) {
      setActiveTab(String(visibleBanks[0].id));
    }
  }, [visibleBanks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same effective-balance logic as the account cards
  const getEffectiveBalance = useCallback((bank: BankAccountDto): number => {
    const bankId = String(bank.id);
    const hasFinal = finalBalanceByBank[bankId] != null;
    const raw = hasFinal
      ? finalBalanceByBank[bankId]!
      : (bank.bookBalance != null ? Number(bank.bookBalance) : 0);
    return (!hasFinal && bank.type === 'CREDIT') ? -Math.abs(raw) : raw;
  }, [finalBalanceByBank]);

  const { bankBalance, cardBalance, netBalance } = useMemo(() => {
    let bankBalance = 0;
    let cardBalance = 0;
    for (const bank of banks) {
      const balance = getEffectiveBalance(bank);
      if (bank.type === 'CREDIT') cardBalance += balance;
      else bankBalance += balance;
    }
    return { bankBalance, cardBalance, netBalance: bankBalance + cardBalance };
  }, [banks, getEffectiveBalance]);

  const activeBank = useMemo(() => {
    return activeTab ? banks.find(b => String(b.id) === activeTab) : null;
  }, [activeTab, banks]);

  const currentOpeningBalance = useMemo(() => {
    return activeTab ? (openingBalanceByBank[activeTab] ?? 0) : 0;
  }, [activeTab, openingBalanceByBank]);

  const unreconciledByBank = useMemo(() => {
    const result: Record<string, number> = {};
    for (const [bankId, txns] of Object.entries(transactionsByBank)) {
      result[bankId] = txns.filter(t => t.status !== "MATCHED").length;
    }
    return result;
  }, [transactionsByBank]);

  const unreconciledCount = activeTab ? (unreconciledByBank[activeTab] || 0) : 0;

  const handlePageChange = (page: number) => {
    if (activeTab && page >= 1 && page <= currentPagination.totalPages) {
      fetchTransactions(activeTab, page, getCurrentFilters(), currentSortOrder);
    }
  };

  const handleViewModeToggle = () => {
    const newMode = viewMode === 'table' ? 'statement' : 'table';
    setViewMode(newMode);
    posthog.capture("banking_view_mode_toggled", { mode: newMode });
    if (activeTab) {
      setTransactionsByBank(prev => { const u = { ...prev }; delete u[activeTab]; return u; });
    }
  };

  const handleMerchantNameChange = (value: string) => {
    setMerchantName(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (activeTab) {
        const filters = getCurrentFilters();
        filters.merchantName = value;
        fetchTransactions(activeTab, 1, filters, currentSortOrder);
      }
    }, 300);
  };

  const handleDirectionFilterChange = (direction: string) => {
    setDirectionFilter(direction);
    posthog.capture("banking_filtered", { filter: "direction", value: direction });
    if (activeTab) {
      const filters = getCurrentFilters();
      filters.direction = direction;
      fetchTransactions(activeTab, 1, filters, currentSortOrder);
    }
  };

  const handleIntercompanyOnlyToggle = () => {
    const next = !intercompanyOnly;
    setIntercompanyOnly(next);
    posthog.capture("banking_filtered", { filter: "intercompany_only", value: next });
    if (activeTab) {
      const filters = getCurrentFilters();
      filters.intercompanyOnly = next;
      fetchTransactions(activeTab, 1, filters, currentSortOrder);
    }
  };

  const clearFilters = () => {
    setMerchantName("");
    setDirectionFilter("all");
    setIntercompanyOnly(false);
    if (activeTab) {
      const resolved = resolveDateFilter(dateFilterKey, dateCustomFrom, dateCustomTo);
      const dateFilters = resolved ? { startDate: resolved.startDate, endDate: resolved.endDate } : undefined;
      fetchTransactions(activeTab, 1, dateFilters, currentSortOrder);
    }
  };

  const handleReconcileClick = (transactionId: string) => {
    setReconcileTransactionId(transactionId);
    setEntityId("");
    setReconcileModalOpen(true);
  };

  const handleReconcileOpen = () => {
    const firstUnreconciled = transactions.find(t => t.status !== "MATCHED");
    if (firstUnreconciled) handleReconcileClick(String(firstUnreconciled.id));
  };

  const handleReconcileConfirm = () => {
    if (reconcileTransactionId && entityId.trim() && activeTab) {
      setTransactionsByBank(prev => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).map(t =>
          String(t.id) === reconcileTransactionId ? { ...t, status: "MATCHED" as const } : t
        )
      }));
      setReconcileModalOpen(false);
      setReconcileTransactionId(null);
      setEntityId("");
    }
  };

  const handleReconcileCancel = () => {
    setReconcileModalOpen(false);
    setReconcileTransactionId(null);
    setEntityId("");
  };

  const fetchCategories = async () => {
    if (categoriesFetched) return;
    try {
      const categories = await apiClient.get<Category[]>('/banking/categories');
      setAllCategories(categories?.map(c => c.name) || []);
      setCategoriesFetched(true);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCategoryChange = (transactionId: string, newCategory: string) => {
    if (activeTab) {
      setTransactionsByBank(prev => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).map(t =>
          String(t.id) === transactionId ? { ...t, category: newCategory } : t
        )
      }));
    }
  };

  return (
    <Card className="m-4 shadow-sm border-border/60">
      <CardContent className="p-6">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground/60 mb-1">
              Command Center
            </p>
            <h1 className="text-[20px] font-medium text-foreground leading-none">
              Banking &amp; Cards
            </h1>
          </div>
          <div className="flex items-center gap-2 pt-0.5">
            <button
              onClick={() => setAccountTypeFilter('banks')}
              className={cn(
                "text-[12px] text-muted-foreground px-3 py-1 rounded-full border transition-colors",
                accountTypeFilter === 'banks'
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-card border-border/60 hover:border-border"
              )}
            >
              Banks <span className="font-medium text-foreground ml-1">{formatAmount(bankBalance)}</span>
            </button>
            <button
              onClick={() => setAccountTypeFilter('cards')}
              className={cn(
                "text-[12px] text-muted-foreground px-3 py-1 rounded-full border transition-colors",
                accountTypeFilter === 'cards'
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-card border-border/60 hover:border-border"
              )}
            >
              Cards <span className={cn("font-medium ml-1", cardBalance < 0 ? "text-rose-600 dark:text-rose-400" : "text-foreground")}>{cardBalance < 0 ? `–${formatAmount(Math.abs(cardBalance))}` : formatAmount(cardBalance)}</span>
            </button>
            <button
              onClick={() => setAccountTypeFilter('all')}
              className={cn(
                "text-[12px] text-muted-foreground px-3 py-1 rounded-full border transition-colors",
                accountTypeFilter === 'all'
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-card border-border/60 hover:border-border"
              )}
            >
              Total <span className={cn("font-medium ml-1", netBalance < 0 ? "text-rose-600 dark:text-rose-400" : "text-foreground")}>{formatAmount(netBalance)}</span>
            </button>
          </div>
        </div>

        {banks.length === 0 && !loading ? (
          <div className="text-center text-muted-foreground py-10 text-sm">
            No bank accounts connected
          </div>
        ) : (
          <>
            {/* ── Account tab strip ──────────────────────────────────────── */}
            <div className="flex gap-2 overflow-x-auto pb-0.5 mb-5 scrollbar-none">
              {visibleBanks.map(bank => {
                const bankId    = String(bank.id);
                const isActive  = activeTab === bankId;
                const iconStyle = getAccountIconStyle(bank);
                const isCard    = bank.type === 'CREDIT';
                const last4     = bank.last4Digits || "——";
                // Prefer the computed running balance (most recent txn); fall back to bookBalance
                const hasFinal  = finalBalanceByBank[bankId] != null;
                const raw       = hasFinal
                  ? finalBalanceByBank[bankId]
                  : (bank.bookBalance != null ? Number(bank.bookBalance) : null);
                // Only negate for CREDIT when falling back to bookBalance (stored as positive liability)
                const balance   = !hasFinal && raw != null && bank.type === 'CREDIT' ? -Math.abs(raw) : raw;
                const isNeg     = balance != null && balance < 0;
                const isZero    = balance === 0;
                const unreconciled = unreconciledByBank[bankId] || 0;

                return (
                  <button
                    key={bankId}
                    onClick={() => { setActiveTab(bankId); posthog.capture("banking_tab_switched", { bank_id: bankId }); }}
                    className={cn(
                      "flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border transition-all flex-shrink-0",
                      isActive
                        ? "shadow-sm"
                        : "border-border/50 bg-card hover:border-border"
                    )}
                    style={isActive
                      ? { background: "#E6F1FB", borderColor: "#378ADD", borderWidth: "1.5px" }
                      : {}}
                  >
                    {/* Icon */}
                    <div
                      className="w-7 h-7 rounded-[7px] flex items-center justify-center flex-shrink-0"
                      style={{ background: iconStyle.bg, color: iconStyle.text }}
                    >
                      {isCard
                        ? <CreditCard className="w-3.5 h-3.5" strokeWidth={2} />
                        : <Landmark className="w-3.5 h-3.5" strokeWidth={2} />
                      }
                    </div>

                    {/* Name + last4 */}
                    <div className="flex flex-col items-start gap-px min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-medium text-foreground truncate max-w-[100px]">
                          {bank.name}
                        </span>
                        {unreconciled > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[17px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex-shrink-0">
                            {unreconciled}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground">····{last4}</span>
                    </div>

                    {/* Balance */}
                    <span className={cn(
                      "text-[12px] font-medium ml-1 tabular-nums",
                      isNeg  ? "text-rose-700 dark:text-rose-400" :
                      isZero ? "text-muted-foreground/60" : "text-foreground"
                    )}>
                      {balance == null ? "—"
                        : isNeg ? `–${formatAmount(Math.abs(balance), bank.currency)}`
                        : formatAmount(balance, bank.currency)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Toolbar + transaction content ──────────────────────────── */}
            {activeTab && (
              <div>
                <TransactionFilters
                  merchantName={merchantName}
                  directionFilter={directionFilter}
                  onMerchantNameChange={handleMerchantNameChange}
                  onDirectionFilterChange={handleDirectionFilterChange}
                  onClearFilters={clearFilters}
                  dateFilterKey={dateFilterKey}
                  dateCustomFrom={dateCustomFrom}
                  dateCustomTo={dateCustomTo}
                  onDateFilterChange={onDateFilterChange}
                  viewMode={viewMode}
                  onViewModeToggle={handleViewModeToggle}
                  unreconciledCount={unreconciledCount}
                  onReconcileOpen={handleReconcileOpen}
                  intercompanyOnly={intercompanyOnly}
                  onIntercompanyOnlyToggle={handleIntercompanyOnlyToggle}
                />

                {viewMode === "statement" ? (
                  <StatementView
                    transactions={transactions}
                    openingBalance={currentOpeningBalance}
                    loading={loading}
                    pagination={currentPagination}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    currency={activeBank?.currency}
                  />
                ) : (
                  <TransactionTable
                    transactions={transactions}
                    onReconcileClick={handleReconcileClick}
                    allCategories={allCategories}
                    onFetchCategories={fetchCategories}
                    onCategoryChange={handleCategoryChange}
                    loading={loading}
                    pagination={currentPagination}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    openingBalance={currentOpeningBalance}
                    currency={activeBank?.currency}
                  />
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <ReconciliationModal
        open={reconcileModalOpen}
        entityId={entityId}
        onEntityIdChange={setEntityId}
        onConfirm={handleReconcileConfirm}
        onCancel={handleReconcileCancel}
      />
    </Card>
  );
};

export default Banking;
